import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UI_COLORS, GRADIENTS, colorHelpers } from '../../utils/colors';
import { usePortalAuth } from '../../context/portalAuthContext';
import portalDataService from '../../services/portalDataService';

/**
 * The "signup" step — the invited (or rejected) customer fills their real
 * profile. PUT /portal/me/profile flips onboarding_status to profile_submitted.
 * gst_number, if given, must be exactly 15 chars (else BE 422).
 */
const PortalOnboardingProfile = () => {
  const { t } = useTranslation();
  const s = t('portal.onboarding.profile', { returnObjects: true });
  const { customer, logout, refreshCustomer } = usePortalAuth();
  const isRejected = (customer?.onboarding_status || '').toLowerCase() === 'rejected';

  const [form, setForm] = useState({
    gst_number: customer?.gst_number || '',
    contact_person: customer?.contact_person || '',
    phone_number_2: customer?.phone_number_2 || '',
    address_1: customer?.address_1 || '',
    address_2: customer?.address_2 || '',
    city: customer?.city || '',
    state: customer?.state || '',
    zip_code: customer?.zip_code || '',
    country: customer?.country || 'India',
    delivery_location: customer?.delivery_location || '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const update = (k, v) => { setForm((p) => ({ ...p, [k]: v })); if (error) setError(''); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setError('');
    // Client-side mirror of the BE rule: GST is optional, but if present it must be 15 chars.
    if (form.gst_number && form.gst_number.trim().length !== 15) {
      setError(s?.errors?.gstLength || 'GST number must be exactly 15 characters.');
      return;
    }
    // Send only non-empty fields.
    const body = {};
    Object.entries(form).forEach(([k, v]) => { if (v !== '' && v !== null && v !== undefined) body[k] = typeof v === 'string' ? v.trim() : v; });

    setBusy(true);
    try {
      const res = await portalDataService.updateProfile(body);
      if (res.success) {
        await refreshCustomer(); // status → profile_submitted → shell routes to Pending
      } else {
        setError(res.error || (s?.errors?.failed) || 'Could not submit your details.');
      }
    } catch (err) {
      setError((s?.errors?.failed) || 'Could not submit your details.');
    } finally {
      setBusy(false);
    }
  };

  const inputStyle = { backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: UI_COLORS.FORMS.INPUT.border, color: UI_COLORS.FORMS.INPUT.text };
  const field = (key, label, opts = {}) => (
    <div>
      <label className="block text-xs font-medium mb-1" style={{ color: UI_COLORS.TEXT.MUTED }}>{label}</label>
      <input type={opts.type || 'text'} value={form[key]} onChange={(e) => update(key, e.target.value)} placeholder={opts.placeholder} className="w-full px-3 py-2 border-2 rounded-lg text-sm" style={inputStyle} />
    </div>
  );

  return (
    <div className="min-h-screen" style={{ backgroundColor: UI_COLORS.BACKGROUNDS.PRIMARY }}>
      <header className="shadow-sm" style={{ background: GRADIENTS.PRIMARY }}>
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <div>
            <div className="text-xs font-medium" style={{ color: colorHelpers.withOpacity(UI_COLORS.BUTTONS.PRIMARY.text, 0.85) }}>{s?.header || 'Complete your profile'}</div>
            <div className="text-lg font-bold" style={{ color: UI_COLORS.BUTTONS.PRIMARY.text }}>{customer?.company_name || customer?.owner_name || '—'}</div>
          </div>
          <button type="button" onClick={logout} className="px-4 py-2 rounded-lg text-sm font-semibold" style={{ backgroundColor: colorHelpers.withOpacity(UI_COLORS.BACKGROUNDS.CARD, 0.2), color: UI_COLORS.BUTTONS.PRIMARY.text }}>
            {s?.logout || 'Logout'}
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6">
        {isRejected && (
          <div className="mb-4 rounded-xl px-4 py-3 text-sm font-medium" style={{ backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.WARNING, 0.1), color: UI_COLORS.STATUS.WARNING, border: `1px solid ${colorHelpers.withOpacity(UI_COLORS.STATUS.WARNING, 0.3)}` }}>
            {s?.rejectedBanner || 'Your details need changes. Please review and resubmit.'}
          </div>
        )}

        <p className="text-sm mb-4" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{s?.intro || 'Tell us a bit about your business. Our team will review and approve your account.'}</p>

        <form onSubmit={handleSubmit} className="space-y-4 rounded-2xl border p-5" style={{ borderColor: UI_COLORS.BORDERS.LIGHT, backgroundColor: UI_COLORS.BACKGROUNDS.CARD }}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {field('gst_number', s?.fields?.gst || 'GST number (15 chars)', { placeholder: '24ABCDE1234F1Z5' })}
            {field('contact_person', s?.fields?.contact || 'Contact person')}
            {field('phone_number_2', s?.fields?.phone2 || 'Alternate phone')}
            {field('delivery_location', s?.fields?.deliveryLocation || 'Delivery location')}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {field('address_1', s?.fields?.address1 || 'Address line 1')}
            {field('address_2', s?.fields?.address2 || 'Address line 2')}
            {field('city', s?.fields?.city || 'City')}
            {field('state', s?.fields?.state || 'State')}
            {field('zip_code', s?.fields?.zip || 'ZIP code')}
            {field('country', s?.fields?.country || 'Country')}
          </div>

          {error && <div className="rounded-lg px-3 py-2 text-sm font-medium" style={{ backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1), color: UI_COLORS.STATUS.ERROR }}>{error}</div>}

          <div className="flex justify-end">
            <button type="submit" disabled={busy} className="px-6 py-3 rounded-xl font-semibold disabled:opacity-60" style={{ background: GRADIENTS.PRIMARY, color: UI_COLORS.BUTTONS.PRIMARY.text }}>
              {busy ? (s?.submitting || 'Submitting…') : (s?.submit || 'Submit for approval')}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default PortalOnboardingProfile;
